import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const overlayVariants = {
    hidden: { opacity: 0, transition: { duration: 0.25 } },
    visible: { opacity: 1, transition: { duration: 0.25 } },
};

const panelVariants = {
    hidden: {
        x: '-100%',
        transition: { duration: 0.3, ease: 'easeInOut' }
    },
    visible: {
        x: '0%',
        transition: {
            duration: 0.3,
            ease: 'easeInOut',
            staggerChildren: 0.06,
            delayChildren: 0.1
        }
    },
};

const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.25, ease: 'easeOut' }
    }
};

const subMenuVariants = {
    hidden: {
        height: 0,
        opacity: 0,
        transition: { duration: 0.2, ease: 'easeInOut' }
    },
    visible: {
        height: 'auto',
        opacity: 1,
        transition: {
            duration: 0.25,
            ease: 'easeInOut',
            staggerChildren: 0.04,
            delayChildren: 0.05
        }
    }
};

const subMenuItemVariants = {
    hidden: { opacity: 0, x: -12 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.2, ease: 'easeOut' }
    }
};

const MenuPanel = ({ isOpen, onClose, currentUser, userInfo, isAdmin, onLoginClick, onLogoutClick, allProducts, bundleSettings }) => {
    const navigate = useNavigate();
    const [isProductsOpen, setIsProductsOpen] = useState(false);
    const { t, lang, toggleLanguage, getCategoryLabel } = useLanguage();

    useEffect(() => {
        if (!isOpen) {
            setIsProductsOpen(false);
        }
    }, [isOpen]);

    const categories = useMemo(() => {
        if (!allProducts) return [];
        const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
        return uniqueCategories.sort();
    }, [allProducts]);
    
    const isRealUser = currentUser && !currentUser.isAnonymous;
    const displayName = userInfo?.name || currentUser?.displayName || currentUser?.email;

    const handleProtectedLinkClick = (path) => {
        if (!isRealUser) {
            onClose();
            onLoginClick();
        } else {
            navigate(path);
            onClose();
        }
    };

    const UserAvatar = () => (
        <div className="h-12 w-12 rounded-full bg-stone-200 flex items-center justify-center">
            <svg className="h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
        </div>
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    variants={overlayVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onClick={onClose}
                >
                    <motion.div 
                        className="fixed left-0 top-0 h-full w-full max-w-sm bg-white/95 md:bg-white/80 md:backdrop-blur-lg shadow-xl flex flex-col p-6 border-r border-white/20" 
                        variants={panelVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-gray-800">{t('menu')}</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={toggleLanguage}
                                    className="px-3 py-1 text-xs font-semibold text-[#8B6939] border border-[#b08d57]/50 rounded-full hover:bg-[#b08d57] hover:text-white transition-colors"
                                >
                                    {t('switchLangLabel')}
                                </button>
                                <button onClick={onClose} className="p-2 -mr-2 rounded-full text-gray-600 hover:bg-black/10 text-2xl" aria-label="Close">&times;</button>
                            </div>
                        </div>

                        <motion.div variants={menuItemVariants} className="mb-6 p-4 bg-black/5 rounded-lg">
                            {isRealUser ? (
                                <div className="flex items-center gap-4">
                                    {currentUser.photoURL ? (
                                        <img loading="lazy" decoding="async" src={currentUser.photoURL} alt="User" className="h-12 w-12 rounded-full" />
                                    ) : (
                                        <UserAvatar />
                                    )}
                                    <div>
                                        <p className="font-semibold text-gray-900">{displayName}</p>
                                        <p className="text-xs text-gray-700">{currentUser.email}</p>
                                    </div>
                                </div>
                            ) : (
                                <button onClick={() => { onClose(); onLoginClick(); }} className="w-full py-3 px-4 bg-[#b08d57] text-white font-semibold rounded-md hover:bg-[#b08d57]/90">
                                    {t('loginRegister')}
                                </button>
                            )}
                        </motion.div>
                        
                        <nav className="flex-grow space-y-2 overflow-y-auto">
                            <motion.div variants={menuItemVariants}>
                                <Link to="/" onClick={onClose} className="block w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors">
                                    {t('home')}
                                </Link>
                            </motion.div>

                            <motion.div variants={menuItemVariants}>
                                <button
                                    onClick={() => setIsProductsOpen(prev => !prev)}
                                    className="flex items-center justify-between w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors"
                                >
                                    <span>{t('allProducts')}</span>
                                    <svg
                                        className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <AnimatePresence>
                                    {isProductsOpen && (
                                        <motion.div
                                            variants={subMenuVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="hidden"
                                            className="overflow-hidden ps-4 space-y-1 mt-1 border-s-2 border-[#b08d57]/30 ms-3"
                                        >
                                            <motion.div variants={subMenuItemVariants}>
                                                <Link
                                                    to="/products"
                                                    onClick={onClose}
                                                    className="block w-full text-start p-2.5 text-base font-medium text-gray-800 hover:bg-black/10 rounded-md transition-colors"
                                                >
                                                    {t('viewAll')}
                                                </Link>
                                            </motion.div>
                                            {bundleSettings?.enabled && (
                                                <motion.div variants={subMenuItemVariants}>
                                                    <Link
                                                        to="/bundles"
                                                        onClick={onClose}
                                                        className="block w-full text-start p-2.5 text-base font-semibold text-[#8B6939] hover:bg-black/10 rounded-md transition-colors"
                                                    >
                                                        {lang === 'ar' ? (bundleSettings.titleAr || 'الباقات والعروض') : (bundleSettings.titleEn || 'Shop Bundles')}
                                                    </Link>
                                                </motion.div>
                                            )}
                                            {categories.map(cat => (
                                                <motion.div key={cat} variants={subMenuItemVariants}>
                                                    <Link
                                                        to={`/products/category/${cat.toLowerCase().replace(/ /g, '-')}`}
                                                        onClick={onClose}
                                                        className="block w-full text-start p-2.5 text-base text-gray-700 hover:bg-black/10 rounded-md capitalize transition-colors"
                                                    >
                                                        {getCategoryLabel(cat)}
                                                    </Link>
                                                </motion.div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>

                            <motion.div variants={menuItemVariants}>
                                <button onClick={() => handleProtectedLinkClick('/profile')} className="block w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors">
                                    {t('myProfile')}
                                </button>
                            </motion.div>

                            <motion.div variants={menuItemVariants}>
                                <button onClick={() => handleProtectedLinkClick('/orders')} className="block w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors">
                                    {t('myOrders')}
                                </button>
                            </motion.div>

                            <motion.div variants={menuItemVariants}>
                                <button onClick={() => handleProtectedLinkClick('/wishlist')} className="block w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors">
                                    {t('myWishlist')}
                                </button>
                            </motion.div>

                            {isAdmin && (
                                <motion.div variants={menuItemVariants}>
                                    <Link to="/admin" onClick={onClose} className="block w-full text-start p-3 text-lg text-gray-800 hover:bg-black/10 rounded-md transition-colors">
                                        {t('adminPanel')}
                                    </Link>
                                </motion.div>
                            )}
                        </nav>

                        {isRealUser && (
                            <motion.div variants={menuItemVariants} className="mt-auto pt-6">
                                <button onClick={() => { onClose(); onLogoutClick(); }} className="w-full py-3 px-4 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300 transition-colors">
                                    {t('logout')}
                                </button>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default React.memo(MenuPanel);
